class ProductImageGallery {
  constructor(element) {
    this.gallery = element;
    this.mainMedia = this.gallery.querySelectorAll('[data-main-media]');
    this.thumbnails = this.gallery.querySelectorAll('[data-thumbnail-trigger]');
    this.carouselPrev = this.gallery.querySelector('[data-carousel-prev]');
    this.carouselNext = this.gallery.querySelector('[data-carousel-next]');
    this.mainPrev = this.gallery.querySelector('[data-main-prev]');
    this.mainNext = this.gallery.querySelector('[data-main-next]');

    // Find the currently active thumbnail index
    this.currentIndex = 0;
    this.thumbnails.forEach((thumb, index) => {
      if (thumb.classList.contains('is-active')) {
        this.currentIndex = index;
      }
    });

    this.hasCarousel = this.carouselPrev !== null && this.carouselNext !== null;
    this.hasMainNav = this.mainPrev !== null && this.mainNext !== null;

    this.init();
  }

  init() {
    this.bindEvents();
    this.updateCarouselVisibility();
    this.ensureImageVisible();
  }

  ensureImageVisible() {
    // Ensure at least one image is visible (fallback for edge cases)
    const visibleMedia = this.gallery.querySelector('[data-main-media].is-visible');
    if (!visibleMedia && this.mainMedia.length > 0) {
      // No visible media, show the first one
      this.mainMedia.forEach((media, index) => {
        if (index === 0) {
          media.classList.remove('is-hidden');
          media.classList.add('is-visible');
        } else {
          media.classList.remove('is-visible');
          media.classList.add('is-hidden');
        }
      });
      // Also update thumbnails
      if (this.thumbnails.length > 0) {
        this.thumbnails.forEach((thumb, index) => {
          if (index === 0) {
            thumb.classList.add('is-active');
          } else {
            thumb.classList.remove('is-active');
          }
        });
      }
    }
  }

  bindEvents() {
    // Thumbnail click events
    this.thumbnails.forEach((thumb, index) => {
      thumb.addEventListener('click', () => this.selectImage(index));
    });

    // Carousel navigation
    if (this.carouselPrev) {
      this.carouselPrev.addEventListener('click', () => this.previousImage());
    }
    if (this.carouselNext) {
      this.carouselNext.addEventListener('click', () => this.nextImage());
    }

    // Main image navigation
    if (this.mainPrev) {
      this.mainPrev.addEventListener('click', (e) => {
        e.preventDefault();
        this.previousImage();
      });
    }
    if (this.mainNext) {
      this.mainNext.addEventListener('click', (e) => {
        e.preventDefault();
        this.nextImage();
      });
    }

    // Keyboard navigation
    this.gallery.addEventListener('keydown', (e) => this.handleKeyboard(e));
  }

  selectImage(index) {
    if (index < 0 || index >= this.thumbnails.length) return;

    const thumbnail = this.thumbnails[index];
    const mediaId = thumbnail.dataset.mediaId;

    // Update active thumbnail
    this.thumbnails.forEach((thumb) => {
      thumb.classList.remove('is-active');
    });
    thumbnail.classList.add('is-active');

    // Scroll active thumbnail into view (smooth scroll)
    this.scrollToActiveThumbnail(thumbnail);

    // Show/hide main media
    this.showMainMedia(mediaId);

    // Update current index
    this.currentIndex = index;

    // Update navigation visibility
    this.updateCarouselVisibility();
  }

  scrollToActiveThumbnail(thumbnail) {
    const thumbnailsContainer = this.gallery.querySelector('.product-image-gallery__thumbnails');
    if (!thumbnailsContainer) return;

    const containerRect = thumbnailsContainer.getBoundingClientRect();
    const thumbnailRect = thumbnail.getBoundingClientRect();
    const scrollLeft = thumbnailsContainer.scrollLeft;
    const containerWidth = containerRect.width;
    const thumbnailLeft = thumbnailRect.left - containerRect.left + scrollLeft;
    const thumbnailWidth = thumbnailRect.width;
    const gap = 12; // 0.75rem gap

    // Calculate scroll position to center the thumbnail
    const scrollPosition = thumbnailLeft - (containerWidth / 2) + (thumbnailWidth / 2);

    thumbnailsContainer.scrollTo({
      left: scrollPosition,
      behavior: 'smooth'
    });
  }

  showMainMedia(mediaId) {
    // Hide all media and show the selected one
    this.mainMedia.forEach((mediaWrapper) => {
      if (mediaWrapper.dataset.mediaId === mediaId) {
        // Show this media
        mediaWrapper.classList.remove('is-hidden');
        mediaWrapper.classList.add('is-visible');
        
        // Remove any fixed height/aspect-ratio constraints in adapt mode
        const container = this.gallery.querySelector('.product-image-gallery__main-container');
        const computedStyle = window.getComputedStyle(container);
        
        // If aspect-ratio is auto or not set, let the media determine the height
        if (container && (computedStyle.aspectRatio === 'auto' || 
            container.style.aspectRatio === 'auto' ||
            (!container.style.aspectRatio && !computedStyle.aspectRatio))) {
          // Remove any fixed height to let media size naturally
          container.style.height = 'auto';
          container.style.minHeight = '0';
        }
      } else {
        // Hide this media
        mediaWrapper.classList.remove('is-visible');
        mediaWrapper.classList.add('is-hidden');
      }
    });
  }

  previousImage() {
    let newIndex;
    if (this.currentIndex > 0) {
      newIndex = this.currentIndex - 1;
    } else {
      // Loop to last image
      newIndex = this.thumbnails.length - 1;
    }
    this.selectImage(newIndex);
  }

  nextImage() {
    let newIndex;
    if (this.currentIndex < this.thumbnails.length - 1) {
      newIndex = this.currentIndex + 1;
    } else {
      // Loop to first image
      newIndex = 0;
    }
    this.selectImage(newIndex);
  }

  updateCarouselVisibility() {
    // With infinite loop, both arrows are always enabled
    const showPrev = true;
    const showNext = true;

    // Update carousel navigation
    if (this.hasCarousel) {
      if (this.carouselPrev) {
        this.carouselPrev.classList.remove('is-hidden');
      }
      if (this.carouselNext) {
        this.carouselNext.classList.remove('is-hidden');
      }
    }

    // Update main navigation arrows
    if (this.hasMainNav) {
      if (this.mainPrev) {
        this.mainPrev.classList.remove('is-hidden');
      }
      if (this.mainNext) {
        this.mainNext.classList.remove('is-hidden');
      }
    }
  }

  handleKeyboard(e) {
    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        this.previousImage();
        break;
      case 'ArrowRight':
        e.preventDefault();
        this.nextImage();
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (document.activeElement?.dataset.thumbnailTrigger !== undefined) {
          const index = Array.from(this.thumbnails).indexOf(document.activeElement);
          if (index !== -1) {
            this.selectImage(index);
          }
        }
        break;
    }
  }
}

// Initialize all product image galleries
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
    const galleryInstance = new ProductImageGallery(gallery);
    // Store instance on element for external access
    gallery.productImageGallery = galleryInstance;
    
    // Listen for variant change events
    gallery.addEventListener('variant:changed', (e) => {
      // The variant picker will handle the image update via switchToImage
      // This listener is here for any additional handling if needed
    });
  });
});
